import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Linking,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { StockEvent } from '@/types/perplexity.types';

interface StockEventsPanelProps {
  ticker: string;
  companyName: string;
  events: StockEvent[];
  isLoading: boolean;
  error: string | null;
  onRefresh?: () => void;
  defaultCollapsed?: boolean;
}

// Helper function to get icon for event type
const getEventIcon = (eventType: StockEvent['event_type']): keyof typeof Ionicons.glyphMap => {
  switch (eventType) {
    case 'earnings':
      return 'cash-outline';
    case 'analyst':
      return 'trending-up-outline';
    case 'executive':
      return 'people-outline';
    case 'product':
      return 'rocket-outline';
    case 'news':
      return 'newspaper-outline';
    default:
      return 'information-circle-outline';
  }
};

// Helper function to get color for sentiment
const getSentimentColor = (sentiment: StockEvent['sentiment']) => {
  switch (sentiment) {
    case 'positive':
      return Colors.market.profit;
    case 'negative':
      return Colors.market.loss;
    default:
      return Colors.text.tertiary;
  }
};

export const StockEventsPanel: React.FC<StockEventsPanelProps> = ({
  ticker,
  companyName,
  events,
  isLoading,
  error,
  onRefresh,
  defaultCollapsed = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const [expandedEventIndex, setExpandedEventIndex] = useState<number | null>(null);

  const handleEventPress = (url: string) => {
    if (url) {
      Linking.openURL(url).catch(err => console.error('Error opening URL:', err));
    }
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Ionicons name="newspaper-outline" size={18} color={Colors.text.secondary} />
          <Text style={styles.headerTitle}>Recent Events</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={Colors.ai.primary} />
          <Text style={styles.loadingText}>Loading {ticker} events...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Pressable onPress={() => setIsExpanded(!isExpanded)} style={styles.header}>
          <View style={styles.headerLeft}>
            <Ionicons name="newspaper-outline" size={18} color={Colors.text.secondary} />
            <Text style={styles.headerTitle}>Recent Events</Text>
          </View>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={Colors.text.tertiary}
          />
        </Pressable>
        {isExpanded && (
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={20} color={Colors.status.error} />
            <Text style={styles.errorText}>{error}</Text>
            {onRefresh && (
              <Pressable onPress={onRefresh} style={styles.retryButton}>
                <Text style={styles.retryText}>Try Again</Text>
              </Pressable>
            )}
          </View>
        )}
      </View>
    );
  }

  if (!events || events.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Pressable onPress={() => setIsExpanded(!isExpanded)} style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="time-outline" size={18} color={Colors.text.secondary} />
          <Text style={styles.headerTitle}>Timeline - {ticker}</Text>
          <View style={styles.eventCount}>
            <Text style={styles.eventCountText}>{events.length}</Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={Colors.text.tertiary}
          />
        </View>
      </Pressable>

      {isExpanded && (
        <ScrollView style={styles.timelineContent} nestedScrollEnabled>
          <View style={styles.timeline}>
            {events.map((event, index) => {
              const isExpanded = expandedEventIndex === index;
              const sentimentColor = getSentimentColor(event.sentiment);
              const eventIcon = getEventIcon(event.event_type);

              return (
                <View key={index} style={styles.timelineItem}>
                  {/* Timeline connector line */}
                  {index < events.length - 1 && <View style={styles.timelineLine} />}

                  {/* Timeline dot with sentiment color */}
                  <View style={[styles.timelineDot, { backgroundColor: sentimentColor }]}>
                    <Ionicons name={eventIcon} size={14} color={Colors.background.primary} />
                  </View>

                  {/* Event card */}
                  <Pressable
                    style={styles.eventCard}
                    onPress={() => setExpandedEventIndex(isExpanded ? null : index)}
                  >
                    {/* Date */}
                    <Text style={styles.eventDate}>{event.date}</Text>

                    {/* Headline with sentiment indicator */}
                    <View style={styles.eventHeader}>
                      <View
                        style={[styles.sentimentIndicator, { backgroundColor: sentimentColor }]}
                      />
                      <Text style={styles.eventHeadline} numberOfLines={isExpanded ? undefined : 2}>
                        {event.headline}
                      </Text>
                    </View>

                    {/* Description (expandable) */}
                    {isExpanded && <Text style={styles.eventDescription}>{event.description}</Text>}

                    {/* Footer with source and link */}
                    <View style={styles.eventFooter}>
                      <View style={styles.eventType}>
                        <Text style={styles.eventTypeText}>{event.event_type}</Text>
                      </View>
                      <Text style={styles.eventSource}>{event.source}</Text>
                      {event.url && (
                        <Pressable
                          onPress={e => {
                            e.stopPropagation();
                            handleEventPress(event.url);
                          }}
                          style={styles.linkButton}
                        >
                          <Ionicons name="open-outline" size={14} color={Colors.accent.secondary} />
                        </Pressable>
                      )}
                    </View>
                  </Pressable>
                </View>
              );
            })}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.secondary,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  eventCount: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  eventCountText: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  refreshButton: {
    padding: 4,
  },
  timelineContent: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    maxHeight: 400,
  },
  timeline: {
    paddingTop: 8,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 16,
    position: 'relative',
  },
  timelineLine: {
    position: 'absolute',
    left: 14,
    top: 32,
    bottom: -16,
    width: 2,
    backgroundColor: Colors.border.default,
  },
  timelineDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    marginTop: 4,
    borderWidth: 3,
    borderColor: Colors.background.secondary,
  },
  eventCard: {
    flex: 1,
    backgroundColor: Colors.background.primary,
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  eventDate: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.tertiary,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  eventHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  sentimentIndicator: {
    width: 3,
    minHeight: 40,
    borderRadius: 2,
    marginTop: 2,
  },
  eventHeadline: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    lineHeight: 20,
  },
  eventDescription: {
    fontSize: 13,
    lineHeight: 19,
    color: Colors.text.secondary,
    marginBottom: 10,
  },
  eventFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  eventType: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  eventTypeText: {
    fontSize: 10,
    fontWeight: '600',
    color: Colors.accent.secondary,
    textTransform: 'capitalize',
  },
  eventSource: {
    flex: 1,
    fontSize: 11,
    color: Colors.text.tertiary,
  },
  linkButton: {
    padding: 4,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    gap: 12,
  },
  loadingText: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    color: Colors.status.error,
  },
  retryButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: Colors.accent.primary,
    borderRadius: 6,
  },
  retryText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.primary,
  },
});
